import { useId, useState } from "react";
import type { Session } from "@spf/contracts";
import { api } from "../lib/client.js";
import { useAsync } from "../lib/useAsync.js";
import { formatDateTime, formatRelativeTime } from "../lib/format.js";
import { describeDevice } from "../lib/device.js";
import { Alert } from "../components/Alert.js";
import { Empty, Failed, Loading, secondaryButtonClass } from "../components/ViewState.js";
import { PageSection } from "../components/Page.js";

interface Props {
  /** Revoking the session you are using ends it, here and everywhere. */
  onCurrentRevoked: () => void;
}

export function SessionsPage({ onCurrentRevoked }: Props) {
  const errorId = useId();
  const [revoking, setRevoking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { state, reload } = useAsync(
    api.sessions,
    "Could not load your sessions. The server may be unreachable.",
  );

  async function revoke(session: Session) {
    setError(null);
    setRevoking(session.id);
    try {
      await api.revokeSession(session.id);
      if (session.current) {
        onCurrentRevoked();
        return;
      }
      reload();
    } catch {
      setError("Could not sign that session out. Try again.");
    } finally {
      setRevoking(null);
    }
  }

  return (
    <PageSection title="Sessions">
      <p className="mb-4 text-sm text-ink-muted">
        Every device signed in to this account. Sign out any you do not recognise.
      </p>

      {error !== null && <Alert id={errorId}>{error}</Alert>}

      {state.kind === "loading" && <Loading label="Loading your sessions…" />}
      {state.kind === "error" && <Failed message={state.message} onRetry={reload} />}
      {state.kind === "ready" && (
        <SessionList
          sessions={state.value.sessions}
          revoking={revoking}
          onRevoke={(session) => void revoke(session)}
        />
      )}
    </PageSection>
  );
}

interface ListProps {
  sessions: Session[];
  revoking: string | null;
  onRevoke: (session: Session) => void;
}

function SessionList({ sessions, revoking, onRevoke }: ListProps) {
  // Only reachable if every session expired between the page loading and this
  // render, but a list that can be empty owes the state.
  if (sessions.length === 0) return <Empty>No active sessions.</Empty>;

  return (
    <ul className="flex flex-col gap-3">
      {sessions.map((session) => (
        <SessionRow
          key={session.id}
          session={session}
          busy={revoking === session.id}
          onRevoke={() => onRevoke(session)}
        />
      ))}
    </ul>
  );
}

interface RowProps {
  session: Session;
  busy: boolean;
  onRevoke: () => void;
}

function SessionRow({ session, busy, onRevoke }: RowProps) {
  return (
    <li className="flex flex-wrap items-start justify-between gap-4 rounded-2xl border border-line p-4">
      <div className="flex min-w-0 flex-col gap-1">
        <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-ink">
          {/* The summary is the convenience; the full string stays as the
              evidence, one hover away. */}
          <span title={session.userAgent ?? undefined}>{describeDevice(session.userAgent)}</span>
          {session.current && (
            <span className="rounded-full bg-btn px-2 py-0.5 text-xs font-medium text-btn-ink">
              This device
            </span>
          )}
        </p>
        <p className="text-xs text-ink-muted">
          {session.ip ?? "Unknown address"} · signed in {formatRelativeTime(session.signedInAt)} ·
          last used {formatRelativeTime(session.lastUsedAt)}
        </p>
        <p className="text-xs text-ink-muted">Expires {formatDateTime(session.expiresAt)}</p>
      </div>
      <button className={secondaryButtonClass} type="button" disabled={busy} onClick={onRevoke}>
        {busy ? "Signing out…" : session.current ? "Sign out here" : "Sign out"}
      </button>
    </li>
  );
}
